<template>
    <div
        class="container-fluid corporate-users-page weeduc-admin-panel page-card text-left p-0"
        :class="{ 'corporate-users-page--dark': dark }"
    >
        <header class="corporate-users-header">
            <div class="corporate-users-header__lead">
                <TitlePage
                    :title="t.users.title"
                    :subtitle="t.users.subtitle"
                    :hasMargin="false"
                />
            </div>
            <div class="corporate-users-header__actions" v-if="canManage">
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="lg"
                    class="text-none"
                    @click="openCreate"
                >
                    <v-icon class="me-1" size="small">mdi-plus</v-icon>
                    {{ t.users.new }}
                </v-btn>
            </div>
        </header>

        <div class="corporate-users-shell">
            <section class="corporate-users-stats">
                <article class="corporate-users-stat">
                    <span class="corporate-users-stat__label">{{ t.users.total }}</span>
                    <strong class="corporate-users-stat__value">{{ stats.total }}</strong>
                </article>
                <article class="corporate-users-stat">
                    <span class="corporate-users-stat__label">{{ t.users.active }}</span>
                    <strong class="corporate-users-stat__value">{{ stats.active }}</strong>
                </article>
                <article class="corporate-users-stat">
                    <span class="corporate-users-stat__label">{{ t.users.responsibles }}</span>
                    <strong class="corporate-users-stat__value">{{ stats.responsibles }}</strong>
                </article>
            </section>

            <div class="corporate-users-panel">
                <div class="corporate-users-toolbar">
                    <v-text-field
                        v-model="filters.search"
                        :label="t.users.search"
                        variant="outlined"
                        density="compact"
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-magnify"
                        class="weeduc-admin-v-field"
                        @update:model-value="debouncedLoad"
                    />
                    <v-select
                        v-model="filters.sectorId"
                        :items="sectorOptions"
                        item-title="text"
                        item-value="value"
                        :label="t.users.sector"
                        variant="outlined"
                        density="compact"
                        hide-details
                        clearable
                        class="weeduc-admin-v-field"
                        @update:model-value="loadUsers"
                    />
                    <v-select
                        v-model="filters.status"
                        :items="statusOptions"
                        item-title="text"
                        item-value="value"
                        :label="t.users.status"
                        variant="outlined"
                        density="compact"
                        hide-details
                        clearable
                        class="weeduc-admin-v-field"
                        @update:model-value="loadUsers"
                    />
                    <v-select
                        v-model="filters.isResponsible"
                        :items="responsibleFilterOptions"
                        item-title="text"
                        item-value="value"
                        :label="t.users.responsibleFilter"
                        variant="outlined"
                        density="compact"
                        hide-details
                        clearable
                        class="weeduc-admin-v-field"
                        @update:model-value="loadUsers"
                    />
                </div>

                <div v-if="loading" class="corporate-users-empty">
                    <Loader :isActive="false" />
                </div>
                <div v-else-if="error" class="corporate-users-empty">{{ t.common.error }}</div>
                <v-table v-else class="custom-table">
                    <thead>
                        <tr>
                            <th>{{ t.users.name }}</th>
                            <th>{{ t.users.email }}</th>
                            <th>{{ t.users.sector }}</th>
                            <th>{{ t.users.role }}</th>
                            <th class="text-center">{{ t.users.responsible }}</th>
                            <th class="text-center">{{ t.users.status }}</th>
                            <th class="text-center">{{ t.common.actions }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="!users.length">
                            <td colspan="7" class="text-center py-6">{{ t.users.empty }}</td>
                        </tr>
                        <tr v-for="user in users" :key="user.id">
                            <td>
                                <strong>{{ user.name }}</strong>
                                <small v-if="user.internalCode" class="d-block text-medium-emphasis">
                                    {{ user.internalCode }}
                                </small>
                            </td>
                            <td>{{ user.email }}</td>
                            <td>{{ user.sectorName || '—' }}</td>
                            <td>{{ user.roleName || '—' }}</td>
                            <td class="text-center">
                                <span
                                    class="corporate-users-chip"
                                    :class="user.isResponsible ? 'is-yes' : 'is-no'"
                                >
                                    {{ user.isResponsible ? t.users.yes : t.users.no }}
                                </span>
                            </td>
                            <td class="text-center">
                                <span
                                    class="corporate-users-chip"
                                    :class="user.status === 'active' ? 'is-on' : 'is-off'"
                                >
                                    {{ user.status === 'active' ? t.users.active : t.users.inactive }}
                                </span>
                            </td>
                            <td class="text-center">
                                <template v-if="canManage">
                                    <v-btn size="small" variant="text" class="text-none" @click="openEdit(user)">
                                        {{ t.users.edit }}
                                    </v-btn>
                                    <v-btn
                                        size="small"
                                        variant="text"
                                        color="error"
                                        class="text-none"
                                        @click="removeUser(user)"
                                    >
                                        {{ t.users.delete }}
                                    </v-btn>
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </v-table>
            </div>
        </div>

        <v-dialog v-model="formOpen" max-width="640" scrollable>
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <div>
                        <h2 class="corporate-dialog__title">{{ formTitle }}</h2>
                        <p class="corporate-dialog__subtitle">{{ t.users.responsibleHint }}</p>
                    </div>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <section class="corporate-dialog__section">
                        <p class="corporate-dialog__section-label">{{ t.common.sectionData }}</p>
                        <div class="corporate-dialog__section-body">
                            <div class="corporate-dialog__row">
                                <v-text-field
                                    v-model="form.name"
                                    :label="t.users.name"
                                    variant="outlined"
                                    density="compact"
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                                <v-text-field
                                    v-model="form.email"
                                    :label="t.users.email"
                                    type="email"
                                    variant="outlined"
                                    density="compact"
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                            </div>
                            <div class="corporate-dialog__row">
                                <v-select
                                    v-model="form.sectorId"
                                    :items="sectorOptions.filter((o) => o.value)"
                                    item-title="text"
                                    item-value="value"
                                    :label="t.users.sector"
                                    variant="outlined"
                                    density="compact"
                                    clearable
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                                <v-select
                                    v-model="form.roleId"
                                    :items="filteredRoleOptions"
                                    item-title="text"
                                    item-value="value"
                                    :label="t.users.role"
                                    variant="outlined"
                                    density="compact"
                                    clearable
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                            </div>
                            <div class="corporate-dialog__row">
                                <v-select
                                    v-model="form.managerUserId"
                                    :items="managerOptions"
                                    item-title="text"
                                    item-value="value"
                                    :label="t.users.manager"
                                    variant="outlined"
                                    density="compact"
                                    clearable
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                                <v-select
                                    v-model="form.status"
                                    :items="statusOptions.filter((o) => o.value)"
                                    item-title="text"
                                    item-value="value"
                                    :label="t.users.status"
                                    variant="outlined"
                                    density="compact"
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                            </div>
                            <div class="corporate-dialog__row">
                                <v-text-field
                                    v-model="form.internalCode"
                                    :label="t.users.internalCode"
                                    variant="outlined"
                                    density="compact"
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                                <v-text-field
                                    v-model="form.admissionDate"
                                    :label="t.users.admissionDate"
                                    type="date"
                                    variant="outlined"
                                    density="compact"
                                    class="weeduc-admin-v-field"
                                    hide-details="auto"
                                />
                            </div>
                            <div class="corporate-users-responsible-box">
                                <v-switch
                                    v-model="form.isResponsible"
                                    color="primary"
                                    hide-details
                                    inset
                                    :label="t.users.responsibleSwitch"
                                />
                                <p class="corporate-dialog__hint">{{ t.users.responsibleHelp }}</p>
                            </div>
                        </div>
                    </section>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn variant="text" class="text-none" @click="formOpen = false">
                        {{ t.common.cancel }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        :loading="saving"
                        @click="saveUser"
                    >
                        {{ t.common.save }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import usersApi from '@/utils/corporate/users';
import organizationalUnitsApi from '@/utils/corporate/organizationalUnits';
import rolesApi from '@/utils/corporate/roles';
import { canManageCorporateStructure } from '@/utils/corporate/permissions';
import { corporateI18n } from '@/i18n/mixins';
import { mapState } from 'vuex';

export default {
    name: 'CorporateUsers',
    mixins: [corporateI18n],
    components: { TitlePage, Loader },
    data() {
        return {
            loading: true,
            error: false,
            saving: false,
            users: [],
            rolesRaw: [],
            sectorOptions: [],
            roleOptions: [],
            managerOptions: [],
            filters: {
                search: '',
                sectorId: null,
                status: null,
                isResponsible: null,
            },
            formOpen: false,
            form: this.emptyForm(),
            debounceTimer: null,
            stats: {
                total: 0,
                active: 0,
                responsibles: 0,
            },
        };
    },
    computed: {
        ...mapState(['token', 'dark']),
        canManage() {
            return canManageCorporateStructure();
        },
        formTitle() {
            return this.form.id ? this.t.users.formTitleEdit : this.t.users.formTitleCreate;
        },
        statusOptions() {
            return [
                { text: this.t.common.filterAll, value: null },
                { text: this.t.users.active, value: 'active' },
                { text: this.t.users.inactive, value: 'inactive' },
            ];
        },
        responsibleFilterOptions() {
            return [
                { text: this.t.common.filterAll, value: null },
                { text: this.t.users.onlyResponsibles, value: true },
                { text: this.t.users.onlyCollaborators, value: false },
            ];
        },
        filteredRoleOptions() {
            if (!this.form.sectorId) return this.roleOptions.filter((o) => o.value);
            return this.roleOptions.filter(
                (o) => !o.value || this.rolesRaw.some((r) => r.id === o.value && r.sectorId === this.form.sectorId),
            );
        },
    },
    async created() {
        await this.loadLookups();
        await this.loadUsers();
    },
    methods: {
        emptyForm() {
            return {
                id: null,
                name: '',
                email: '',
                sectorId: null,
                roleId: null,
                managerUserId: null,
                isResponsible: false,
                status: 'active',
                internalCode: '',
                admissionDate: null,
            };
        },
        async loadLookups() {
            const [sectors, roles] = await Promise.all([
                organizationalUnitsApi.list(this.token, { type: 'sector' }),
                rolesApi.list(this.token),
            ]);
            this.rolesRaw = roles.content || [];
            this.sectorOptions = [
                { text: this.t.common.filterAll, value: null },
                ...(sectors.content || []).map((s) => ({ text: s.name, value: s.id })),
            ];
            this.roleOptions = [
                { text: '—', value: null },
                ...(roles.content || []).map((r) => ({ text: r.name, value: r.id })),
            ];
            this.managerOptions = [
                { text: '—', value: null },
                ...((sectors.managers) || []).map((m) => ({ text: m.name, value: m.id })),
            ];
        },
        async loadUsers() {
            this.loading = true;
            this.error = false;
            try {
                const [filtered, all] = await Promise.all([
                    usersApi.list(this.token, {
                        ...this.filters,
                        search: this.filters.search || undefined,
                        size: 200,
                    }),
                    usersApi.list(this.token, { size: 500 }),
                ]);
                this.users = filtered.content || [];
                const allUsers = all.content || [];
                this.stats = {
                    total: allUsers.length,
                    active: allUsers.filter((u) => u.status === 'active').length,
                    responsibles: allUsers.filter((u) => u.isResponsible).length,
                };
            } catch (e) {
                console.error(e);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        debouncedLoad() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => this.loadUsers(), 300);
        },
        openCreate() {
            this.form = this.emptyForm();
            this.formOpen = true;
        },
        openEdit(user) {
            this.form = {
                id: user.id,
                name: user.name,
                email: user.email,
                sectorId: user.sectorId,
                roleId: user.roleId,
                managerUserId: user.managerUserId,
                isResponsible: Boolean(user.isResponsible),
                status: user.status || 'active',
                internalCode: user.internalCode || '',
                admissionDate: user.admissionDate || null,
            };
            this.formOpen = true;
        },
        async saveUser() {
            if (!this.form.name?.trim() || !this.form.email?.trim()) return;
            this.saving = true;
            try {
                if (this.form.id) {
                    await usersApi.update(this.token, this.form.id, this.form);
                } else {
                    await usersApi.create(this.token, this.form);
                }
                this.formOpen = false;
                await this.loadLookups();
                await this.loadUsers();
            } catch (e) {
                alert(e.message || this.t.common.error);
            } finally {
                this.saving = false;
            }
        },
        async removeUser(user) {
            if (!window.confirm(this.t.users.deleteConfirm)) return;
            try {
                await usersApi.remove(this.token, user.id);
                await this.loadLookups();
                await this.loadUsers();
            } catch (e) {
                alert(e.message || this.t.common.error);
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.corporate-users-page {
    background: #f4f5f8;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(37, 8, 145, 0.06);
    width: 100%;
}

.corporate-users-header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.5rem 1rem;
    background: linear-gradient(135deg, #fafbff 0%, #ffffff 100%);
    border-bottom: 1px solid #e8eaf0;
    border-radius: 16px 16px 0 0;
}

.corporate-users-header__lead :deep(.titlelayoutGeneric) {
    border-bottom: none;
    padding-bottom: 0;
}

.corporate-users-header__lead :deep(.titlelayoutGeneric h3) {
    font-size: 1.5rem;
    margin: 0 0 0.35rem;
    color: #1a1d26;
}

.corporate-users-shell {
    padding: 1.25rem 1.5rem 1.5rem;
    height: calc(100vh - 210px);
    overflow-y: auto;
    scrollbar-width: none;
}

.corporate-users-shell::-webkit-scrollbar {
    display: none;
}

.corporate-users-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
    margin-bottom: 1.15rem;
}

.corporate-users-stat {
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    padding: 1rem 1.15rem;
    text-align: left;
}

.corporate-users-stat__label {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #5c6370;
    margin-bottom: 0.2rem;
}

.corporate-users-stat__value {
    font-size: 1.4rem;
    font-weight: 700;
    color: #160266;
}

.corporate-users-panel {
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    padding: 1.15rem 1.25rem 1.35rem;
}

.corporate-users-toolbar {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr 1fr;
    gap: 0.75rem;
    margin-bottom: 1.15rem;
    padding: 1rem;
    background: #fafbfc;
    border: 1px solid #f0f2f5;
    border-radius: 12px;
}

.corporate-users-empty {
    min-height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5c6370;
}

.corporate-users-chip {
    display: inline-block;
    font-size: 0.72rem;
    font-weight: 600;
    padding: 0.2rem 0.5rem;
    border-radius: 999px;
}

.corporate-users-chip.is-yes,
.corporate-users-chip.is-on {
    background: rgba(46, 125, 50, 0.12);
    color: #2e7d32;
}

.corporate-users-chip.is-no {
    background: #eef0f3;
    color: #5c6370;
}

.corporate-users-chip.is-off {
    background: rgba(198, 40, 40, 0.1);
    color: #c62828;
}

.corporate-users-form-hint {
    opacity: 1 !important;
    color: #5c6370 !important;
}

.corporate-users-responsible-box {
    border: 1px solid #e8eaf0;
    border-radius: 12px;
    padding: 0.85rem 1rem;
    background: #fafbff;
}

.corporate-users-responsible-box p {
    margin: 0.35rem 0 0;
    font-size: 0.8rem;
    color: #5c6370;
    line-height: 1.4;
}

@media (max-width: 960px) {
    .corporate-users-toolbar,
    .corporate-users-stats {
        grid-template-columns: 1fr;
    }
}

.dashboard.dark .corporate-users-page {
    background: #1c1d24;
    box-shadow: none;
}

.dashboard.dark .corporate-users-header {
    background: linear-gradient(135deg, #252730 0%, #201e26 100%);
    border-bottom-color: #2d2d2d;
}

.dashboard.dark .corporate-users-header__lead :deep(.titlelayoutGeneric h3) {
    color: #f1f3f7;
}

.dashboard.dark .corporate-users-stat,
.dashboard.dark .corporate-users-panel {
    background: #252730;
    border-color: #353945;
}

.dashboard.dark .corporate-users-stat strong {
    color: #e8eaf0;
}

.dashboard.dark .corporate-users-empty,
.dashboard.dark .corporate-users-form-hint,
.dashboard.dark .corporate-users-responsible-box p {
    color: #9ca3b4 !important;
}

.dashboard.dark .corporate-users-chip.is-no {
    background: #2a2e3a;
    color: #9ca3b4;
}

.dashboard.dark .corporate-users-chip.is-yes,
.dashboard.dark .corporate-users-chip.is-on {
    background: rgba(46, 125, 50, 0.22);
    color: #86efac;
}

.dashboard.dark .corporate-users-chip.is-off {
    background: rgba(198, 40, 40, 0.2);
    color: #fca5a5;
}

.dashboard.dark .corporate-users-responsible-box {
    background: #201e26;
    border-color: #353945;
}
</style>
